import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, assetUrl, thumbUrl } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import ShareButton from "@/components/ShareButton";
import { useSeo } from "@/lib/seo";
import {
  MagnifyingGlass, Sparkle, MapPin, ArrowRight, Broadcast, Warning, Camera,
  CaretRight, Leaf, ForkKnife, Buildings, Airplane, Mountains, Cube, Images,
  SealCheck, ArrowUpRight, SpinnerGap, PlayCircle,
  Fingerprint, Clock, NotePencil, ShieldCheck, Stack, UploadSimple,
} from "@phosphor-icons/react";

const HERO_IMAGES = ["/hero/sky-1.jpg", "/hero/sky-2.jpg", "/hero/sky-3.jpg"];
const QUICK_TAGS = [
  "sunset over Kerala backwaters", "snowy Himalayan peaks", "red birds in Oregon",
  "street food in Mumbai", "Taj Mahal at sunrise", "my family photos from 1985",
];

const SISTER_SITES = [
  { label: "NaturePulse", href: "/nature-pulse", color: "#2D6A4F", blurb: "Wildlife • Plants • Landscapes • Observations" },
  { label: "WeatherPulse", href: "/photos", color: "#2563EB", blurb: "Weather • Storms • Sky • Climate" },
  { label: "TravelerPulse", href: "/photos", color: "#F27A54", blurb: "Destinations • Attractions • Journeys" },
  { label: "FoodPulse", href: "/explore?category=Food", color: "#D97706", blurb: "Food • Ingredients • Recipes • Culture" },
  { label: "SocialPulse", href: "/explore?category=Events", color: "#EC4899", blurb: "People • Communities • Events • Stories" },
  { label: "Indias.com", href: "/photos", color: "#0E7490", blurb: "States • Districts • Cities • Heritage" },
];

const FLOW_STEPS = [
  { label: "Upload once", icon: UploadSimple },
  { label: "AI enrichment", icon: Sparkle },
  { label: "Location · Date · Subject", icon: MapPin },
  { label: "Permanent Photo ID", icon: Fingerprint },
  { label: "Reused across the Pulse network", icon: Broadcast },
];

const VALUE_PROPS = [
  { icon: MagnifyingGlass, title: "Find any photo — just describe it", desc: "Search naturally: “elephants near a river” or “my Kerala trip in 2018.” No folders, no filenames." },
  { icon: Fingerprint, title: "Upload once. Use everywhere.", desc: "Each image gets a permanent Photo ID, referenced across Pulse sites without duplicate copies." },
  { icon: Sparkle, title: "AI understands your photos", desc: "Identifies subjects, places, plants, animals and landmarks — you review and correct." },
  { icon: MapPin, title: "Where was this taken?", desc: "GPS, city, district, state and nearby landmarks preserved and linked to place pages." },
  { icon: Clock, title: "When was this moment?", desc: "Original capture dates kept, building visual timelines across days, decades or generations." },
  { icon: NotePencil, title: "Tell the story behind the photo", desc: "Add titles, memories, people, events and historical context so future generations know why it mattered." },
  { icon: ShieldCheck, title: "Your photos. Your choice.", desc: "Keep them private, share with select people, publish publicly or contribute to the network." },
  { icon: Stack, title: "Build a visual legacy", desc: "Old family photos, disappearing landscapes and village traditions become lasting records." },
];

const CAT_META = {
  Nature: { icon: Leaf, color: "#2D6A4F" },
  Travel: { icon: Airplane, color: "#F27A54" },
  Food: { icon: ForkKnife, color: "#D97706" },
  Geographic: { icon: Mountains, color: "#0E7490" },
  Institutions: { icon: Buildings, color: "#7C3AED" },
  Events: { icon: Broadcast, color: "#DC2626" },
  Objects: { icon: Cube, color: "#4B5563" },
  Other: { icon: Sparkle, color: "#F27A54" },
};

function licenseLabel(p) {
  const r = p.reuse_permission;
  if (r === "public_attribution") return "CC · Attribution";
  if (r === "commercial") return "Commercial OK";
  if (r === "public_display") return "Editorial";
  if (r === "all_socialpulse") return "Network";
  return "Canonical";
}

function SectionHeader({ eyebrow, title, sub, to, cta }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        {eyebrow && <p className="overline flex items-center gap-1.5 text-[11px] text-[#F27A54]">{eyebrow}</p>}
        <h2 className="mt-1 font-display text-2xl font-black tracking-tight sm:text-3xl">{title}</h2>
        {sub && <p className="mt-1 max-w-xl text-sm text-zinc-500">{sub}</p>}
      </div>
      {to && (
        <Link to={to} data-testid={`home-cta-${(cta || "view").toLowerCase().replace(/\s+/g, "-")}`}
          className="group flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-bold text-zinc-700 transition-colors hover:border-[#F27A54] hover:text-[#F27A54]">
          {cta || "View all"} <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  );
}

function PhotoTile({ p }) {
  const to = p.photo_id ? `/photo/${p.photo_id}` : `/p/${p.id}`;
  const place = [p.location?.city, p.location?.country].filter(Boolean).join(", ");
  const caption = p.caption || p.description;
  return (
    <div className="mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`home-photo-${p.id}`}>
      <Link to={to} className="group relative block overflow-hidden bg-zinc-100">
        <img src={thumbUrl(p.image_url)} alt={p.alt_text || p.title || "photo"} loading="lazy"
          className="w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
        {p.media_type === "video" && <PlayCircle size={40} weight="fill" className="absolute inset-0 m-auto text-white/90 drop-shadow" />}
        {p.photo_id && <span className="absolute left-2.5 top-2.5 rounded-full bg-black/55 px-2 py-0.5 font-mono text-[9px] font-semibold text-white/90 backdrop-blur-sm">{p.photo_id}</span>}
        <span className="absolute right-2.5 top-2.5 rounded-full bg-white/90 px-2 py-0.5 text-[9px] font-bold text-[#2D6A4F]">{licenseLabel(p)}</span>
      </Link>
      <div className="p-3.5">
        <Link to={to}><p className="line-clamp-1 font-display text-[15px] font-black leading-tight hover:text-[#F27A54]">{p.title || "Untitled"}</p></Link>
        {caption && <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-zinc-500">{caption}</p>}
        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border/60 pt-2.5">
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-1 text-[11px] font-semibold text-zinc-700">
              <Camera size={12} weight="fill" className="shrink-0 text-[#F27A54]" />
              <span className="truncate">{p.photographer || p.submitter?.name || "PhotoPulse"}</span>
            </span>
            {place && <span className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground"><MapPin size={10} weight="fill" /> <span className="truncate">{place}</span></span>}
          </span>
          <ShareButton type="photo" id={p.id} title={p.title} variant="icon" />
        </div>
      </div>
    </div>
  );
}

export default function ExploreHome() {
  const [q, setQ] = useState("");
  const [heroIdx, setHeroIdx] = useState(0);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [fPlace, setFPlace] = useState("");
  const [fLicense, setFLicense] = useState("");
  const [fMedia, setFMedia] = useState("");
  const [facets, setFacets] = useState({ categories: [], places: [], total: 0 });
  const [places, setPlaces] = useState([]);
  const [events, setEvents] = useState([]);
  const [picks, setPicks] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [fresh, setFresh] = useState([]);
  const resultsRef = useRef(null);

  useSeo({
    title: "PhotoPulse.pro — The canonical visual library",
    description: "Search millions of canonical photos in plain language. Explore trending places, categories, event archives and featured photographers.",
  });

  useEffect(() => {
    api.get("/public/gallery/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/public/locations").then((r) => setPlaces(r.data.countries || [])).catch(() => {});
    api.get("/public/event-sets?limit=8").then((r) => setEvents(r.data.events || [])).catch(() => {});
    api.get("/public/editors-picks").then((r) => setPicks(r.data.photos || r.data || [])).catch(() => {});
    api.get("/public/photographers?limit=6").then((r) => setPhotographers(r.data.photographers || [])).catch(() => {});
    api.get("/public/gallery?sort=recent&limit=40").then((r) => setFresh(r.data.photos || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const runSearch = useCallback(async (query, overrides = {}) => {
    const term = (query ?? q).trim();
    if (!term) { setResults(null); return; }
    const f = { place: fPlace, license: fLicense, media_type: fMedia, ...overrides };
    setSearching(true);
    setResults((prev) => ({ query: term, photos: prev?.photos || [], loading: true }));
    try {
      const params = new URLSearchParams({ q: term, limit: "40" });
      if (f.place) params.set("place", f.place);
      if (f.license) params.set("license", f.license);
      if (f.media_type) params.set("media_type", f.media_type);
      const { data } = await api.get(`/public/semantic-search?${params.toString()}`, { timeout: 30000 });
      setResults({ query: term, photos: data.photos || [], loading: false });
    } catch {
      setResults({ query: term, photos: [], loading: false, error: true });
    } finally {
      setSearching(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [q, fPlace, fLicense, fMedia]);

  const clearFilters = () => {
    setFPlace(""); setFLicense(""); setFMedia("");
    if (results?.query) runSearch(results.query, { place: "", license: "", media_type: "" });
  };

  const catCards = (facets.categories || []).slice(0, 6).map((c) => {
    const rep = fresh.find((p) => (p.domain || "").toLowerCase() === c.name.toLowerCase());
    return { ...c, image: rep ? rep.image_url : null };
  });

  const bento = (picks.length ? picks : fresh).slice(0, 5);

  return (
    <PublicLayout>
      {/* ---------- HERO: semantic search ---------- */}
      <section className="relative isolate overflow-hidden bg-[#0c1420]" data-testid="home-hero">
        {HERO_IMAGES.map((src, i) => (
          <img key={src} src={src} alt="" aria-hidden={i !== heroIdx}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-in-out ${i === heroIdx ? "opacity-100" : "opacity-0"}`} />
        ))}
        {/* airy, non-chocolate scrim: soft neutral for legibility + fade into the page */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/25" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#FAF8F5] to-transparent" />
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 py-10 text-center sm:py-14">
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white/90 backdrop-blur-sm">
            <Sparkle size={12} weight="fill" /> The visual memory of the Pulse network
          </span>
          <h1 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl [text-shadow:0_1px_2px_rgba(0,0,0,.35),0_4px_24px_rgba(0,0,0,.4)]">
            Every Photo Has a Story. Keep It Alive.
          </h1>
          <p className="mt-3 max-w-2xl text-sm font-medium text-white sm:text-base [text-shadow:0_1px_10px_rgba(0,0,0,.5)]">
            Your intelligent visual library for moments, places, people and discoveries. Upload once, enrich with AI, find anything in plain language, and share it across the Pulse network.
          </p>

          <form onSubmit={(e) => { e.preventDefault(); runSearch(); }} className="mt-6 w-full max-w-2xl" data-testid="home-search-form">
            <div className="flex items-center gap-2 rounded-full border border-white/30 bg-white/95 p-2 shadow-2xl backdrop-blur-md focus-within:ring-2 focus-within:ring-[#F27A54]">
              <MagnifyingGlass size={22} weight="bold" className="ml-3 shrink-0 text-[#F27A54]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="home-search-input"
                placeholder="Describe what you're looking for…"
                className="min-w-0 flex-1 bg-transparent py-2.5 text-base text-zinc-800 outline-none placeholder:text-zinc-400" />
              <span className="hidden items-center gap-1 rounded-full bg-[#2D6A4F]/10 px-2.5 py-1 text-[10px] font-bold text-[#2D6A4F] sm:flex">
                <Sparkle size={11} weight="fill" /> AI Visual Search
              </span>
              <button type="submit" disabled={searching} data-testid="home-search-submit"
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#F27A54] px-5 py-2.5 text-sm font-bold text-white transition-transform active:scale-95 hover:bg-[#d9633f] disabled:opacity-60">
                {searching ? <SpinnerGap size={16} className="animate-spin" /> : "Search Photos"}
              </button>
            </div>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="home-quick-tags">
            {QUICK_TAGS.map((t) => (
              <button key={t} onClick={() => { setQ(t); runSearch(t); }} data-testid={`home-tag-${t.replace(/\s+/g, "-")}`}
                className="rounded-full border border-white/30 bg-black/30 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md transition-colors hover:bg-[#F27A54] hover:border-[#F27A54]">
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- One photo → one permanent ID → many stories ---------- */}
      <div className="border-b border-border bg-white" data-testid="home-flow">
        <div className="mx-auto max-w-6xl px-5 py-5">
          <p className="overline text-center text-[11px] text-[#F27A54]">One photo → one permanent ID → many stories</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:gap-1.5">
            {FLOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="flex items-center gap-2 sm:gap-1.5">
                  <span className="flex items-center gap-1.5 rounded-full border border-border bg-[#FAF8F5] px-3 py-1.5 text-xs font-bold text-zinc-700">
                    <Icon size={14} weight="fill" className="text-[#F27A54]" /> {s.label}
                  </span>
                  {i < FLOW_STEPS.length - 1 && <ArrowRight size={14} weight="bold" className="hidden text-zinc-300 sm:block" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-16 px-5 py-14">
        {/* ---------- Ecosystem: one library, one network ---------- */}
        <section data-testid="home-ecosystem">
          <SectionHeader eyebrow={<><Broadcast size={12} weight="fill" /> One visual library</>}
            title="One Visual Library. An Entire Network."
            sub="A photo contributed once can enrich stories, places and communities throughout the Pulse ecosystem." />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {SISTER_SITES.map((s) => (
              <Link key={s.label} to={s.href} data-testid={`home-sister-${s.label.toLowerCase()}`}
                className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                <div className="min-w-0">
                  <p className="flex items-center gap-1 font-display text-sm font-black text-zinc-800 group-hover:text-[#F27A54]">
                    {s.label} <ArrowUpRight size={13} className="text-zinc-300 transition-colors group-hover:text-[#F27A54]" />
                  </p>
                  <p className="mt-0.5 text-[11px] leading-snug text-zinc-500">{s.blurb}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ---------- Trending places ---------- */}
        {places.length > 0 && (
          <section data-testid="home-places">
            <SectionHeader eyebrow={<><MapPin size={12} weight="fill" /> Trending places</>}
              title="Explore by place" sub="Canonical photo records across World › Country › City."
              to="/photos" cta="All locations" />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {places.slice(0, 8).map((c) => (
                <Link key={c.slug} to={`/photos/${c.slug}`} data-testid={`home-place-${c.slug}`}
                  className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-zinc-100 transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-xl">
                  {c.hero_image && <img src={thumbUrl(c.hero_image)} alt={c.name} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                  <span className="absolute right-2.5 top-2.5 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold text-zinc-700">{c.count} photos</span>
                  <div className="absolute inset-x-0 bottom-0 p-3.5">
                    <p className="font-display text-lg font-black text-white drop-shadow-sm">{c.name}</p>
                    <p className="flex items-center gap-1 text-[11px] font-semibold text-white/80">Explore <CaretRight size={10} weight="bold" /></p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Photo gallery (captioned) / search results — kept prominent ---------- */}
        <section ref={resultsRef} data-testid="home-gallery">
          {results ? (
            <SectionHeader eyebrow={<><Sparkle size={12} weight="fill" /> Semantic results</>}
              title={`“${results.query}”`}
              sub={results.loading ? "Searching…" : `${results.photos.length} matches ranked by meaning`} />
          ) : (
            <SectionHeader eyebrow={<><Images size={12} weight="fill" /> Photo gallery</>}
              title="Newest in the library" sub="Every photo is a canonical record — click any card for its full page, license and download options." to="/explore" cta="Browse all" />
          )}

          {results && (
            <button onClick={() => { setResults(null); setQ(""); clearFilters(); }} data-testid="home-clear-search"
              className="mb-4 text-sm font-semibold text-[#F27A54] hover:underline">← Back to latest</button>
          )}

          {results && (
            <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="home-filters">
              <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">Refine</span>
              <select value={fPlace} data-testid="filter-place"
                onChange={(e) => { setFPlace(e.target.value); runSearch(results.query, { place: e.target.value }); }}
                className="h-9 rounded-full border border-border bg-white px-3 text-xs font-semibold text-zinc-700 outline-none focus:border-[#F27A54]">
                <option value="">All places</option>
                {(facets.places || []).slice(0, 25).map((p) => <option key={p.name} value={p.name}>{`${p.name} (${p.count})`}</option>)}
              </select>
              <select value={fLicense} data-testid="filter-license"
                onChange={(e) => { setFLicense(e.target.value); runSearch(results.query, { license: e.target.value }); }}
                className="h-9 rounded-full border border-border bg-white px-3 text-xs font-semibold text-zinc-700 outline-none focus:border-[#F27A54]">
                <option value="">Any license</option>
                <option value="commercial">Commercial OK</option>
                <option value="public_attribution">Attribution</option>
                <option value="public_display">Editorial</option>
              </select>
              <div className="flex overflow-hidden rounded-full border border-border" data-testid="filter-media">
                {[["", "All"], ["photo", "Photos"], ["video", "Videos"]].map(([val, lbl]) => (
                  <button key={val || "all"} data-testid={`filter-media-${val || "all"}`}
                    onClick={() => { setFMedia(val); runSearch(results.query, { media_type: val }); }}
                    className={`px-3 py-1.5 text-xs font-bold transition-colors ${fMedia === val ? "bg-[#F27A54] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>{lbl}</button>
                ))}
              </div>
              {(fPlace || fLicense || fMedia) && (
                <button onClick={clearFilters} data-testid="filter-clear" className="text-xs font-semibold text-[#F27A54] hover:underline">Clear filters</button>
              )}
            </div>
          )}

          {results?.loading ? (
            <div className="grid place-items-center py-16"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" weight="bold" /></div>
          ) : results && results.error ? (
            <p className="rounded-2xl border border-dashed border-border py-14 text-center text-sm text-muted-foreground" data-testid="home-search-error">
              Search took too long. <button onClick={() => runSearch(results.query)} className="font-semibold text-[#F27A54] hover:underline">Try again</button>
            </p>
          ) : results && results.photos.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border py-14 text-center text-sm text-muted-foreground" data-testid="home-no-results">
              No matches for “{results.query}”. Try describing colors, subjects, mood or place.
            </p>
          ) : (
            <div className="columns-2 gap-4 sm:columns-3 lg:columns-4" data-testid="home-masonry">
              {(results ? results.photos : fresh).map((p) => <PhotoTile key={p.id} p={p} />)}
            </div>
          )}
        </section>

        {/* ---------- Categories ---------- */}
        {catCards.length > 0 && (
          <section data-testid="home-categories">
            <SectionHeader eyebrow={<><Images size={12} weight="fill" /> Browse by category</>}
              title="Every kind of story" to="/explore" cta="Browse gallery" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {catCards.map((c) => {
                const meta = CAT_META[c.name] || CAT_META.Other;
                const Icon = meta.icon;
                return (
                  <Link key={c.name} to={`/explore?category=${encodeURIComponent(c.name)}`} data-testid={`home-cat-${c.name}`}
                    className="group relative flex aspect-[16/9] items-end overflow-hidden rounded-2xl bg-zinc-800 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                    {c.image && <img src={thumbUrl(c.image)} alt={c.name} loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105 group-hover:opacity-90" />}
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${meta.color}dd, ${meta.color}22 55%, transparent)` }} />
                    <div className="relative flex items-center gap-2.5 p-4">
                      <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/20 backdrop-blur-sm"><Icon size={18} weight="fill" className="text-white" /></span>
                      <div>
                        <p className="font-display text-base font-black text-white drop-shadow-sm">{c.name}</p>
                        <p className="text-[11px] font-semibold text-white/80">{c.count} photos</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------- Event photo sets ---------- */}
        {events.length > 0 && (
          <section data-testid="home-events">
            <SectionHeader eyebrow={<><Broadcast size={12} weight="fill" /> Newsroom</>}
              title="Event photo sets" sub="Disasters & news, grouped into pullable PULSE-EVT archives." />
            <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {events.map((e) => (
                <Link key={e.evt_id} to={`/event/${e.evt_id}`} data-testid={`home-event-${e.evt_id}`}
                  className="group relative w-72 shrink-0 overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative aspect-[16/9] overflow-hidden bg-zinc-100">
                    {e.cover_image && <img src={thumbUrl(e.cover_image)} alt={e.title} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                    <span className={`absolute left-2.5 top-2.5 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${e.type === "disaster" ? "bg-red-600" : "bg-[#2D6A4F]"}`}>
                      {e.type === "disaster" && <Warning size={10} weight="fill" />} {e.type}
                    </span>
                  </div>
                  <div className="p-3.5">
                    <p className="font-mono text-[10px] text-zinc-400">{e.evt_id}</p>
                    <p className="mt-0.5 line-clamp-1 font-display text-base font-black">{e.title}</p>
                    <p className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                      {e.severity && <span className="rounded-full bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700">{e.severity}</span>}
                      {e.location?.country && <span className="flex items-center gap-0.5"><MapPin size={10} weight="fill" /> {e.location.country}</span>}
                      <span>· {e.photo_count} photos</span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Why PhotoPulse (value props) ---------- */}
        <section data-testid="home-why">
          <SectionHeader eyebrow={<><Sparkle size={12} weight="fill" /> Why PhotoPulse</>}
            title="Photos with meaning. Memories with context."
            sub="Find any moment. Understand every image. Connect it to the people, places and stories that matter." />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE_PROPS.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} data-testid={`home-why-${v.title.slice(0, 14).replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#F27A54]/10"><Icon size={20} weight="fill" className="text-[#F27A54]" /></span>
                  <p className="mt-3 font-display text-sm font-black leading-tight text-zinc-800">{v.title}</p>
                  <p className="mt-1.5 text-[12px] leading-snug text-zinc-500">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ---------- Editor's picks bento ---------- */}
        {bento.length >= 3 && (
          <section data-testid="home-picks">
            <SectionHeader eyebrow={<><SealCheck size={12} weight="fill" /> Editor’s picks</>}
              title="Standouts this week" />
            <div className="grid auto-rows-[180px] grid-cols-2 gap-4 sm:grid-cols-4">
              {bento.map((p, i) => (
                <Link key={p.id} to={p.photo_id ? `/photo/${p.photo_id}` : `/p/${p.id}`} data-testid={`home-pick-${p.id}`}
                  className={`group relative overflow-hidden rounded-2xl bg-zinc-100 ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                  <img src={thumbUrl(p.image_url)} alt={p.title || ""} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  {p.photo_id && <span className="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-0.5 font-mono text-[9px] text-white/90 opacity-0 transition-opacity group-hover:opacity-100">{p.photo_id}</span>}
                  <p className="absolute bottom-2 left-3 text-xs font-bold text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-100">{p.photographer || p.submitter?.name || "PhotoPulse"}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Featured photographers ---------- */}
        {photographers.length > 0 && (
          <section data-testid="home-photographers">
            <SectionHeader eyebrow={<><Camera size={12} weight="fill" /> Featured photographers</>}
              title="The people behind the frames" to="/photographers" cta="All creators" />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {photographers.slice(0, 6).map((ph) => (
                <Link key={ph.key} to={`/photographer/${encodeURIComponent(ph.key)}`} data-testid={`home-photographer-${ph.key}`}
                  className="group rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-[#F27A54]/15 font-display text-base font-black text-[#F27A54]">
                      {ph.avatar_url ? <img src={assetUrl(ph.avatar_url)} alt="" className="h-full w-full object-cover" /> : (ph.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1 truncate font-display text-sm font-black">{ph.name} <SealCheck size={13} weight="fill" className="text-[#2D6A4F]" /></p>
                      <p className="text-[11px] text-muted-foreground">{ph.count} canonical photos</p>
                    </div>
                    <ArrowUpRight size={16} className="ml-auto shrink-0 text-zinc-300 transition-colors group-hover:text-[#F27A54]" />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {(ph.thumbs || []).slice(0, 3).map((t, i) => (
                      <div key={i} className="aspect-square overflow-hidden rounded-lg bg-zinc-100">
                        <img src={thumbUrl(t)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      </div>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
        {/* ---------- Contribute CTA ---------- */}
        <section data-testid="home-contribute" className="overflow-hidden rounded-3xl bg-[#0c1420] px-6 py-10 text-center sm:px-10">
          <h2 className="font-display text-2xl font-black text-white sm:text-3xl">Contribute to something bigger</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-white/70">
            Your photograph might help document a species on NaturePulse, illustrate a destination on TravelerPulse, preserve a traditional dish on FoodPulse or tell the story of a village on Indias.com.
          </p>
          <Link to="/login" data-testid="home-contribute-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F27A54] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#d9633f]">
            Start contributing <ArrowRight size={16} weight="bold" />
          </Link>
        </section>
      </div>
    </PublicLayout>
  );
}
